import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import TextButton from 'LuxKit/Button/TextButton'
import LineHotAirBalloonIcon from 'LuxKit/Icons/line/LineHotAirBalloonIcon'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Group from 'components/utils/Group'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import LesaPurchaseSummarySeparator from './Common/LesaPurchaseSummarySeparator'
import BodyText from 'LuxKit/Typography/BodyText'
import Action from '../../Chat/MessageMeta/Action'
import { filterActions } from '../../../utils/actionsUtils'
import LesaPurchaseSummaryTitle from './Common/LesaPurchaseSummaryTitle'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import { ChatActions } from 'api/supportAssistant'
import useExperienceAirportTransferPurchaseDetails from 'hooks/Experiences/useExperienceAirportTransferPurchaseDetails'

interface Props {
  order: App.Order
  isPastOrder: boolean
  hideSelect?: boolean
  actions?: Array<App.ChatMessageMetaContent>
  payload?: App.ChatMessageMetaPayload
  componentsMap: Map<string | undefined, App.ChatMessageMetaContent>
  canEdit: boolean
}

function LesaExperienceAirportTransferSummary(props: Props) {
  const intl = useIntl()
  const { order, isPastOrder, hideSelect, actions, payload, componentsMap, canEdit } = props

  const dispatch = useAppDispatch()

  const {
    location,
    name,
    bookingNumber,
    experienceDate,
    experienceTime,
  } = useExperienceAirportTransferPurchaseDetails(order, isPastOrder)

  const orderActions = actions?.filter(action => filterActions(action, isPastOrder, ['listInclusions', 'showFinePrint', 'changeRoom']))

  const action: ChatActions = ChatActions.AIRPORT_TRANSFERS_SELECT_ORDER

  const handleSelectClick = useCallback(() => {
    dispatch(addChatMessage('', { action, payload: { id_orders: order.id, canEditOrder: canEdit } }))
  }, [dispatch, order.id, action, canEdit])

  const isListOrSelectOrderAction = !!componentsMap?.get('list-orders') || !!componentsMap?.get('list-orders-with-airport-transfers') || !!componentsMap?.get('selectOrder') || !!componentsMap?.get('airportTransfersSelectOrder')

  return (
    <>
      {isListOrSelectOrderAction && (
        <LesaPurchaseSummaryContainer>
          <LesaPurchaseSummaryHeader order={order}>
            <BodyTextBlock lineClamp={2} variant="small" startIcon={<LineHotAirBalloonIcon size="XS" />}>{intl.formatMessage({
              id: '8-7JlHURSC4p3Buvbryh',
              defaultMessage: 'Experience',
              description: 'Category label for experience airport transfer purchase summary',
            })}
            </BodyTextBlock>
            {!isPastOrder && <StatusLabel variant="confirmation">{intl.formatMessage({
              id: '5lw-558kwQ9QYI6P_6Ye',
              defaultMessage: 'Upcoming',
              description: 'Status label for upcoming experience airport transfer booking',
            })}
            </StatusLabel>}
            {isPastOrder && <StatusLabel variant="pending">{intl.formatMessage({
              id: '8Juppvz4DOA3imuolY3S',
              defaultMessage: 'Past',
              description: 'Status label for past experience airport transfer booking',
            })}
            </StatusLabel>}
          </LesaPurchaseSummaryHeader>
          <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={16}>
            <Group direction="vertical" gap={4}>
              {location && <CaptionTextBlock
                variant="medium"
                colour="light-foreground-alt"
              >{location}
              </CaptionTextBlock>}
              <LesaPurchaseSummaryTitle orderId={order.id}>
                {name}
              </LesaPurchaseSummaryTitle>
              <LesaPurchaseSummarySeparator>
                {experienceDate && <BodyText as="span" variant="small" colour="light-foreground-alt">{experienceDate}</BodyText>}
                {experienceTime && <BodyText as="span" variant="small" colour="light-foreground-alt">{experienceTime}</BodyText>}
              </LesaPurchaseSummarySeparator>
              {bookingNumber && <BodyTextBlock variant="small" colour="light-foreground-alt"><strong>{intl.formatMessage({
                id: 'EJfypVfwzXhP7P-tWszd',
                defaultMessage: 'Booking ID:',
                description: 'Label for the experience airport transfer booking ID',
              })}
              </strong> {bookingNumber}
              </BodyTextBlock>}
            </Group>
            {!hideSelect &&
              <TextButton onClick={handleSelectClick} fit="mobile-full-width" size="small" kind="secondary">
                {intl.formatMessage({
                  id: 'RB26A4Y3cJ192p-l7qD6',
                  defaultMessage: 'Select',
                  description: 'Button text to select an experience airport transfer booking',
                })}
              </TextButton>}
          </Group>
        </LesaPurchaseSummaryContainer>
      )}
      {!!orderActions?.length &&
        <Group fullWidth wrap="wrap" horizontalAlign="start" direction="vertical" tabletDirection="horizontal" gap={8}>
          {orderActions.map((action) => (
            <Action key={action.action?.key} action={action} payload={payload} />
          ))}
        </Group>}
    </>
  )
}

export default LesaExperienceAirportTransferSummary
